import React, { useCallback, useEffect } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import {
  AlignItems,
  Box,
  Display,
  DisplayPosition,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'
import type * as braintree from 'braintree-web'
import { Loader } from 'goustouicomponents'
import type {
  AuthorizationData,
  AuthorizationResponse,
  ButtonLabelOption,
  FlowType,
} from 'paypal-checkout-components'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { trackUTMAndPromoCode as trackEvent } from 'actions/tracking'
import {
  canPayWithPayPalExpress,
  clickPayPalExpressAgreeContinue,
  clickPayWithPayPalExpress,
  closePayPalExpressModal,
} from 'actions/trackingKeys'
import {
  fireCheckoutError,
  firePayPalError,
  savePayPalExpressCheckoutBillingAddress,
  savePayPalExpressCheckoutDeliveryAddress,
  savePayPalExpressCheckoutPersonalInfo,
  setCurrentPaymentMethod,
  setPayPalNonce,
} from 'routes/Checkout/checkoutActions'
import { isPayPalReady } from 'routes/Checkout/checkoutPaymentSelectors'
import { getPayPalErrors } from 'routes/Checkout/checkoutSelectors'
import { PaymentMethod } from 'routes/Signup/signupConfig'
import { getBasketPostcode } from 'selectors/basket'

import { validateShippingPostcode } from './utils'

declare const paypal: typeof import('paypal-checkout-components')

// This is the PayPal Express checkout component - EPIC: https://gousto.atlassian.net/browse/BEET-517

type PayPalExpressProps = {
  submit: () => void
  hide: boolean
  isPayPalInitialized: boolean
  setIsPayPalInitialized: (value: boolean) => void
  braintreePayPalCheckoutComponent: braintree.PayPalCheckout | null
}

export const PayPalExpress = ({
  submit,
  hide,
  isPayPalInitialized,
  setIsPayPalInitialized,
  braintreePayPalCheckoutComponent,
}: PayPalExpressProps) => {
  const isPayPalPaymentApproved = useSelector(isPayPalReady)

  const hasErrors = useSelector(getPayPalErrors).size > 0
  const isLoading = !hasErrors && !isPayPalInitialized
  const dispatch = useDispatch()
  const basketPostcode = useSelector(getBasketPostcode)

  const initPayPal = useCallback(async () => {
    if (!braintreePayPalCheckoutComponent) return
    const loadPayPalSDK = async () => {
      await braintreePayPalCheckoutComponent.loadPayPalSDK({
        vault: true,
        commit: false,
        currency: 'GBP',
        intent: 'tokenize',
      })
    }

    const createPayment = () =>
      braintreePayPalCheckoutComponent.createPayment({
        flow: 'vault' as FlowType,
        locale: 'en_GB',
        billingAgreementDescription: 'Gousto recipe box subscription',
        enableShippingAddress: true,
        shippingAddressEditable: true,
      })

    const fetchPayPalNonce = async (approveData: AuthorizationData) => {
      try {
        const payload = await braintreePayPalCheckoutComponent.tokenizePayment(approveData)

        if (!payload) {
          throw new Error('PayPal tokenization returned no payload')
        }

        const { details: customerDetails, nonce } = payload

        dispatch(setCurrentPaymentMethod(PaymentMethod.PayPalExpressCheckout))

        // Ensure shipping address postcode matches the basket postcode
        const validation = validateShippingPostcode({
          shippingAddress: customerDetails.shippingAddress,
          basketPostcode,
        })

        if (!validation.isValid) {
          datadogLogs.logger.error('PayPal shipping validation failed', {
            message: validation.error,
          })
          dispatch(fireCheckoutError(actionTypes.PAYPAL_EXPRESS_SHIPPING_POSTCODE_INVALID))
          dispatch(firePayPalError(validation.error))

          return null
        }

        dispatch(savePayPalExpressCheckoutDeliveryAddress(customerDetails.shippingAddress))
        dispatch(savePayPalExpressCheckoutPersonalInfo(customerDetails))
        dispatch(savePayPalExpressCheckoutBillingAddress(customerDetails.shippingAddress))

        dispatch(setPayPalNonce(nonce))
        datadogLogs.logger.info(
          'PayPal nonce is fetched successfully, proceed to submitting the form',
        )
        submit()

        return payload
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        datadogLogs.logger.error('Fetching PayPal nonce has failed', err)
        dispatch(firePayPalError(err))
      }

      return null
    }

    const renderPayPalButton = () => {
      const buttonsConfig = {
        fundingSource: 'paypal',
        createBillingAgreement: () => {
          dispatch(trackEvent(clickPayWithPayPalExpress))
          datadogRum.addAction('click_pay_with_paypal_express')
          datadogLogs.logger.info('PayPal signup attempt')

          return createPayment()
        },
        onInit: () => {
          dispatch(trackEvent(canPayWithPayPalExpress))
        },
        onApprove: (data: AuthorizationData) => {
          dispatch(trackEvent(clickPayPalExpressAgreeContinue))
          const payload = fetchPayPalNonce(data) as Promise<AuthorizationResponse>
          datadogLogs.logger.info('PayPal onApprove')

          return payload
        },
        onCancel: () => {
          dispatch(trackEvent(closePayPalExpressModal))
        },
        onError: (error: unknown) => {
          const err = error instanceof Error ? error : new Error('Unknown error')
          datadogLogs.logger.error('Error in rendering PayPal Button', err)
          dispatch(firePayPalError(err))
        },
        style: {
          height: 48,
          label: 'pay' as ButtonLabelOption,
          tagline: false,
        },
      }

      return paypal.Buttons(buttonsConfig).render('#paypal-container')
    }

    try {
      await loadPayPalSDK()
      renderPayPalButton()
      setIsPayPalInitialized(true)
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error('Unknown error')
      datadogLogs.logger.error('PayPal initialization failed', err)
      dispatch(firePayPalError(err))
    }
    // Don't include submit as currently it is reinstantiating on every render causing
    // initPaypal to be called multiple times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, braintreePayPalCheckoutComponent])

  useEffect(() => {
    if (braintreePayPalCheckoutComponent) {
      initPayPal()
    }
  }, [braintreePayPalCheckoutComponent, initPayPal])

  return (
    <>
      <Box display={hide || isPayPalPaymentApproved ? Display.None : Display.Block} width="100%">
        <Box position={DisplayPosition.Relative} height="48px">
          <Box
            id="paypal-container"
            data-testid="paypalExpressContainer"
            height={isPayPalInitialized ? 'auto' : '0'}
            style={isPayPalInitialized ? {} : { visibility: 'hidden', opacity: '0' }}
          />
          {isLoading && (
            <Box
              data-testid="loaderContainer"
              position={DisplayPosition.Absolute}
              top="0px"
              left="0px"
              height="100%"
              width="100%"
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
            >
              <Loader color="Bluecheese" />
            </Box>
          )}
        </Box>
      </Box>
      <Space size={[4, 5]} direction="vertical" />
    </>
  )
}
